import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { CRMConversationFilter } from "@jugl-web/rest-api/integrations/types";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { getChatSecondsToExpiration } from "../../ClientSupportChat/utils/getChatSecondsToExpiration";
import { useClientSupportPageContext } from "../../../providers/ClientSupportPageProvider/useClientSupportPageContext";

type UseSelectedConversationParams = {
  channel: CRMChannel;
  selectedFilter?: CRMConversationFilter;
  setSelectedFilter?: (value: CRMConversationFilter) => void;
};

export const useSelectedConversation = ({
  channel: hookChannel,
  selectedFilter,
  setSelectedFilter,
}: UseSelectedConversationParams) => {
  const { channel: urlChannel, activeConversation } = useParams<string>();
  const { selectedConversation, selectConversation } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const isInitializedRef = useRef<boolean>(false);

  if (selectedConversation && !isInitializedRef.current) {
    isInitializedRef.current = true;
  }

  const shouldFetch =
    !isInitializedRef.current &&
    !!activeConversation &&
    !!entityId &&
    hookChannel === urlChannel;

  const queryArgs = {
    entityId,
    convId: activeConversation ?? "",
  };

  const { data: whatsappConversation } =
    integrationsApi.useGetWhatsappConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.whatsapp,
    });

  const { data: instagramConversation } =
    integrationsApi.useGetInstagramConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.instagram,
    });

  const { data: webChatBotConversation } =
    integrationsApi.useGetWebChatBotConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.chatbot,
    });

  const { data: twilioConversation } =
    integrationsApi.useGetTwilioConversationByIdQuery(queryArgs, {
      skip: !shouldFetch || hookChannel !== CRMChannel.twilio,
    });

  const fetchedConversation =
    whatsappConversation ??
    instagramConversation ??
    webChatBotConversation ??
    twilioConversation;

  useEffect(() => {
    if (isInitializedRef.current || !activeConversation || !fetchedConversation)
      return;

    if (
      setSelectedFilter &&
      selectedFilter &&
      (hookChannel === CRMChannel.whatsapp ||
        hookChannel === CRMChannel.instagram)
    ) {
      const isBlocked =
        hookChannel === CRMChannel.whatsapp &&
        typeof fetchedConversation.payload === "object" &&
        fetchedConversation.payload !== null &&
        "action" in fetchedConversation.payload &&
        fetchedConversation.payload.action === "blocked";

      const nextFilter = isBlocked
        ? CRMConversationFilter.blocked
        : getChatSecondsToExpiration(
            "since" in fetchedConversation ? fetchedConversation.since : null
          ) > 0
        ? CRMConversationFilter.active
        : CRMConversationFilter.expired;

      if (nextFilter !== selectedFilter) {
        setSelectedFilter(nextFilter);
      }
    }

    selectConversation(fetchedConversation);
    isInitializedRef.current = true;
  }, [
    activeConversation,
    fetchedConversation,
    hookChannel,
    selectedFilter,
    selectConversation,
    setSelectedFilter,
  ]);
};
